import { useCallback, useEffect, useRef } from 'react'
import { useGlobalEvent } from '@highlight/ipc'
import { setOverlayInteractive } from '@highlight/ui'
import { usePeekAffordance } from '@renderer/overlay/hooks/use-peek-affordance'
import type { PeekSource, SidepanelState } from '@renderer/overlay/atoms'
import { trackSidepanelClosed, trackSidepanelOpened } from '@renderer/overlay/utils/track-sidepanel'

const DEFAULT_PEEK_DELAY = 0
const DEFAULT_PEEK_CLOSE_TIMEOUT = 2000
// Short grace after the cursor leaves the edge before a peek retracts — long
// enough to travel onto the trigger (its hover cancels the hide), short enough
// that pulling away feels immediate instead of waiting out the close timeout.
const PEEK_RETRACT_GRACE = 300

interface SidepanelPeekOptions {
  sidepanelState: SidepanelState
  setSidepanelState: (state: SidepanelState) => void
  setPeekSource: (source: PeekSource | null) => void
  /**
   * Finishes the cleanup an interrupted close animation will never run. Called
   * by every path here that reopens the panel mid-exit.
   */
  finishExitCleanup: () => void
}

/**
 * The edge-peek lifecycle: the native edge tap's peek / show / hide / cancel
 * events, the timers that retract a peek, and the hover exceptions that keep
 * one alive while the user reaches for the chevron.
 *
 * Self-contained because every one of its timers can cancel every other one —
 * splitting the peek delay from the retract delay from the trigger's hover
 * grace is exactly how a peek ends up closing out from under the cursor.
 */
export function useSidepanelPeek({
  sidepanelState,
  setSidepanelState,
  setPeekSource,
  finishExitCleanup,
}: SidepanelPeekOptions) {
  const peekTimeout = useRef<NodeJS.Timeout>()
  const peekCloseTimeout = useRef<NodeJS.Timeout>()
  // Whether the cursor is currently over the peek trigger. Tracked from the
  // trigger's hover events (cancelHide on enter, restartHide on leave) so
  // cancelPeek can tell "cursor left the edge to grab the trigger" apart from
  // "cursor left the edge entirely".
  const triggerHoveredRef = useRef(false)

  // Is there a proximity zone keeping the chevron on screen as the cursor travels
  // toward it? Drives cancelPeek's retract delay below. At low sensitivity the
  // zone is 0, so the chevron is visible *only* while peeking — nothing keeps it
  // on screen once the peek retracts.
  const { peekAffordanceActive } = usePeekAffordance()

  const cancelPendingHide = useCallback(() => {
    clearTimeout(peekCloseTimeout.current)
  }, [])

  const peek = useCallback(() => {
    finishExitCleanup()

    clearTimeout(peekTimeout.current)
    clearTimeout(peekCloseTimeout.current)
    if (sidepanelState === 'hidden') {
      peekTimeout.current = setTimeout(() => {
        setPeekSource('edge')
        setSidepanelState('peeking')
      }, DEFAULT_PEEK_DELAY)
    }
  }, [sidepanelState, setSidepanelState, setPeekSource, finishExitCleanup])

  const show = useCallback(() => {
    finishExitCleanup()

    clearTimeout(peekTimeout.current)
    clearTimeout(peekCloseTimeout.current)
    trackSidepanelOpened('edge')
    setSidepanelState('open')
  }, [setSidepanelState, finishExitCleanup])

  const hide = useCallback(() => {
    clearTimeout(peekTimeout.current)
    clearTimeout(peekCloseTimeout.current)
    setOverlayInteractive(false)
    trackSidepanelClosed('edge')
    setSidepanelState('hidden')
  }, [setSidepanelState])

  // Schedule a peek → hidden transition after `delay` ms, replacing any pending
  // hide so the latest caller wins. Every peek-hide path routes through here.
  const schedulePeekHide = useCallback(
    (delay: number) => {
      clearTimeout(peekCloseTimeout.current)
      peekCloseTimeout.current = setTimeout(() => {
        setOverlayInteractive(false)
        setSidepanelState('hidden')
      }, delay)
    },
    [setSidepanelState],
  )

  // Native "left edge" fired (cursor pulled away). Cancel any pending pre-peek,
  // and if we're already peeking, schedule a retract.
  const cancelPeek = useCallback(() => {
    clearTimeout(peekTimeout.current)
    if (sidepanelState !== 'peeking') return
    // Cursor is on the trigger: its hover already cleared the close timer, and a
    // native left-edge event here just means the cursor stepped off the very edge
    // to reach the (inset) trigger. Re-arming a retract would close the peek out
    // from under the user; their eventual mouseleave (restartHide) handles it.
    if (triggerHoveredRef.current) return
    // The short grace only works when the proximity affordance keeps the chevron
    // on screen as the user travels toward it. Without it (low sensitivity, where
    // the chevron is visible only while peeking) a 300ms retract on the first
    // inward move off the edge kills the peek before it's usable — so fall back to
    // the normal close timeout and let the user reach the inset chevron.
    schedulePeekHide(peekAffordanceActive ? PEEK_RETRACT_GRACE : DEFAULT_PEEK_CLOSE_TIMEOUT)
  }, [sidepanelState, schedulePeekHide, peekAffordanceActive])

  // Auto-hide after entering peeking state
  useEffect(() => {
    if (sidepanelState !== 'peeking') return
    schedulePeekHide(DEFAULT_PEEK_CLOSE_TIMEOUT)
    return () => clearTimeout(peekCloseTimeout.current)
  }, [sidepanelState, schedulePeekHide])

  // Clear peek timeout when sidepanel opens (override reset lives in useSidepanelMotionOverride)
  useEffect(() => {
    if (sidepanelState === 'open') {
      clearTimeout(peekCloseTimeout.current)
    }
  }, [sidepanelState])

  // Native edge detection via aries CGEventTap
  useGlobalEvent('sidepanel:peek', peek)
  useGlobalEvent('sidepanel:show', show)
  useGlobalEvent('sidepanel:hide', hide)
  useGlobalEvent('sidepanel:cancelPeek', cancelPeek)

  const onPanelMouseEnter = useCallback(() => {
    if (sidepanelState !== 'peeking') {
      clearTimeout(peekTimeout.current)
      clearTimeout(peekCloseTimeout.current)
    }
  }, [sidepanelState])

  // Allow peek trigger to cancel hide timeout when hovered
  const handleCancelHide = useCallback(() => {
    triggerHoveredRef.current = true
    clearTimeout(peekTimeout.current)
    clearTimeout(peekCloseTimeout.current)
  }, [])
  useGlobalEvent('sidepanel:cancelHide', handleCancelHide)

  // Restart hide timeout when cursor leaves the peek trigger
  const handleRestartHide = useCallback(() => {
    triggerHoveredRef.current = false
    if (sidepanelState !== 'peeking') return
    schedulePeekHide(DEFAULT_PEEK_CLOSE_TIMEOUT)
  }, [sidepanelState, schedulePeekHide])
  useGlobalEvent('sidepanel:restartHide', handleRestartHide)

  return { onPanelMouseEnter, cancelPendingHide }
}
